import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {DateTime} from 'luxon'

import {
  AggregatedActivity,
  AggregatedLabReading,
  BarChartData,
  BasicPatientData,
  ChartData,
  HealthDataResolution,
  HealthDataType,
  LabReadingType,
  UnitSystem,
} from '../models'
import {getResolutionDateDiff, resolutionUnitMap} from './dates'
import {
  getHealthDataGrade,
  getGlucoseGrade,
  getHba1cGrade,
  getTotalCholesterolGrade,
} from './grades'
import {convertHealthDataValue} from './healthData'
import {
  kgToPounds,
  cmToInches,
  MGDL_IN_MMOLL,
  CHOLESTEROL_MGDL_PER_MMOLL,
} from './units'

export const useResolutionOptions = () => {
  const {t} = useTranslation<string>()

  return useMemo(
    () => [
      {
        value: HealthDataResolution.MONTH,
        label: t('Month'),
        shortLabel: t('M', {context: 'month'}),
      },
      {
        value: HealthDataResolution.WEEK,
        label: t('Week'),
        shortLabel: t('W', {context: 'week'}),
      },
      {
        value: HealthDataResolution.DAY,
        label: t('Day'),
        shortLabel: t('D', {context: 'day'}),
      },
    ],
    [t],
  )
}

export const getChartTickValues: (
  resolution: HealthDataResolution,
  startDate: DateTime,
  dataPointCount: number,
) => Date[] = (resolution, startDate, dataPointCount) =>
  [...Array(dataPointCount - 1)].reduce(
    (arr) => {
      const tick = DateTime.fromJSDate(arr[arr.length - 1])
        .plus(getResolutionDateDiff(resolution))
        .toJSDate()

      arr.push(tick)
      return arr
    },
    [startDate.startOf(resolutionUnitMap[resolution]).toJSDate()],
  )

const healthDataPropertyNameMap: Record<
  HealthDataType,
  keyof Omit<AggregatedActivity, 'period'>
> = {
  [HealthDataType.ACTIVELY_BURNED_CALORIES]: 'activelyBurnedCalories',
  [HealthDataType.ALCOHOL_SCORE]: 'alcoholScore',
  [HealthDataType.BLOOD_PRESSURE]: 'bloodPressureSystolic',
  [HealthDataType.GLUCOSE_MMOL_L]: 'avgGlucoseMmolL',
  [HealthDataType.BMI]: 'bmi',
  [HealthDataType.DIET_SCORE]: 'dietScore',
  [HealthDataType.BODY_FAT_PERCENTAGE]: 'bodyFatPercentage',
  [HealthDataType.CHOLESTEROL_HDL_RATIO]: 'cholesterolHdlRatio',
  [HealthDataType.TOTAL_CHOLESTEROL_MMOL_L]: 'totalCholesterolMmolL',
  [HealthDataType.HDL_MMOL_L]: 'hdlMmolL',
  [HealthDataType.LDL_MMOL_L]: 'ldlMmolL',
  [HealthDataType.TRIGLYCERIDES_MMOL_L]: 'triglyceridesMmolL',
  [HealthDataType.HEART_AGE_YEARS]: 'heartAgeYears',
  [HealthDataType.HEIGHT_CM]: 'heightCm',
  [HealthDataType.MOOD_SCORE]: 'moodScore',
  [HealthDataType.MOVEMENT_SCORE]: 'movementScore',
  [HealthDataType.PEAK_FLOW]: 'peakFlow',
  [HealthDataType.RESTING_HEART_RATE]: 'restingHeartRate',
  [HealthDataType.SLEEP_SCORE]: 'sleepScore',
  [HealthDataType.QUALITY_SLEEP_SECONDS]: 'qualitySleepSeconds',
  [HealthDataType.STEPS]: 'steps',
  [HealthDataType.TOBACCO_SCORE]: 'tobaccoScore',
  [HealthDataType.VO2_MAX]: 'vo2Max',
  [HealthDataType.ACTIVE_MINUTES]: 'activeMinutes',
  [HealthDataType.WAIST_CIRCUMFERENCE_CM]: 'waistCircumferenceCm',
  [HealthDataType.WATER_CONSUMPTION_ML]: 'waterConsumptionMl',
  [HealthDataType.WEIGHT_KG]: 'weightKg',
  [HealthDataType.HEART_RATE_VARIABILITY_MS]: 'heartRateVariabilityMs',
  [HealthDataType.OXYGEN_SATURATION_PERCENT]: 'oxygenSaturationPercent',
  [HealthDataType.RESPIRATION_RATE]: 'respirationRate',
  [HealthDataType.STRESS_INDEX]: 'stressIndex',
}

export const getHealthDataSelector =
  (healthDataType: HealthDataType) => (data: AggregatedActivity) => {
    const key = healthDataPropertyNameMap[healthDataType]
    return data[key]
  }

const convertChartValueForUnitSystem = (
  value: number | undefined,
  healthDataType: HealthDataType,
  unitSystem?: UnitSystem,
) => {
  if (value == null || unitSystem !== UnitSystem.IMPERIAL) {
    return value
  }

  switch (healthDataType) {
    case HealthDataType.WEIGHT_KG:
      return kgToPounds(value)
    case HealthDataType.WAIST_CIRCUMFERENCE_CM:
    case HealthDataType.HEIGHT_CM:
      return cmToInches(value)
    default:
      return value
  }
}

export const formatChartData = (
  data: AggregatedActivity[],
  healthDataType: HealthDataType,
  user?: BasicPatientData,
  unitSystem?: UnitSystem,
) => {
  const dataSelector = getHealthDataSelector(healthDataType)
  const bmiDataSelector = getHealthDataSelector(HealthDataType.BMI)

  const getGrade = (record: AggregatedActivity) =>
    healthDataType === HealthDataType.WEIGHT_KG
      ? getHealthDataGrade(HealthDataType.BMI, bmiDataSelector(record), user)
      : getHealthDataGrade(healthDataType, dataSelector(record), user)

  return data
    .map((record) => ({
      y: convertChartValueForUnitSystem(
        convertHealthDataValue(dataSelector(record), healthDataType),
        healthDataType,
        unitSystem,
      ),
      date: DateTime.fromISO(record.period).toJSDate(),
      grade: getGrade(record),
    }))
    .filter((item) => item.y != null) as ChartData
}

export const formatBloodPressureChartData = (
  data: AggregatedActivity[],
  user?: BasicPatientData,
) =>
  data
    .map((record) => ({
      date: DateTime.fromISO(record.period).toJSDate(),
      y: record.bloodPressureSystolic,
      y0: record.bloodPressureDiastolic,
      grade: getHealthDataGrade(
        HealthDataType.BLOOD_PRESSURE,
        record.bloodPressureSystolic,
        user,
      ),
    }))
    .filter((item) => item.y != null && item.y0 != null) as BarChartData

export const formatGlucoseChartData = (
  data: AggregatedActivity[],
  unitSystem?: UnitSystem,
) => {
  const toMgdl = (value?: number) => {
    if (value == null) {
      return undefined
    }
    return unitSystem === UnitSystem.IMPERIAL ? value / MGDL_IN_MMOLL : value
  }

  return data
    .map((record) => ({
      date: DateTime.fromISO(record.period).toJSDate(),
      y: toMgdl(record.maxGlucoseMmolL),
      y0: toMgdl(record.minGlucoseMmolL),
      avgY: toMgdl(record.avgGlucoseMmolL),
      grade: getGlucoseGrade(
        record.minGlucoseMmolL,
        record.maxGlucoseMmolL,
        record.avgGlucoseMmolL,
      ),
    }))
    .filter(
      (item) => item.y != null && item.y0 != null && item.avgY != null,
    ) as BarChartData
}

export const formatAggregatedLabReadingsChartData = (
  data: AggregatedLabReading[],
  labType: LabReadingType,
  unitSystem?: UnitSystem,
): ChartData =>
  data.map((record) => ({
    date: DateTime.fromISO(record.period).toJSDate(),
    y:
      labType === 'TOTAL_CHOLESTEROL' && unitSystem === UnitSystem.IMPERIAL
        ? record.value * CHOLESTEROL_MGDL_PER_MMOLL
        : record.value,
    grade:
      labType === 'HBA1C'
        ? getHba1cGrade(record.value)
        : getTotalCholesterolGrade(record.value),
  }))
